/**
 * Iter53 P36 — ImportProgressWidget
 *
 * Self-contained live-progress widget for a running import batch.
 *
 * Uses the lightweight `/api/import/batches/{id}/live-progress` endpoint (added in
 * P36) which returns only counters + server-computed ETA/rate — payload ~350 bytes
 * (vs ~5-500KB for the full `/import/batches/{id}` endpoint that also includes
 * error_details, row_outcomes, client_summary etc.).
 *
 * Props:
 *   - batchId (string, required)
 *   - onFinish (fn, optional): called with the terminal payload when status
 *     transitions to completed/partial/failed/cancelled.
 *   - onCancel (fn, optional): shown as a red "Anulează" button; skipped if omitted.
 *   - intervalMs (number, default 1500): polling cadence.
 *   - showTitle (bool, default true): render the filename + status header.
 *   - compact (bool, default false): use tighter padding + smaller text.
 *   - testIdPrefix (string, default "import-progress-widget"): namespace for data-testid.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Progress } from "./ui/progress";
import { Button } from "./ui/button";
import { RefreshCw, CheckCircle2, XCircle, AlertCircle, Ban } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDuration = (s) => {
  if (s === null || s === undefined) return "—";
  const n = Math.max(0, Math.round(s));
  if (n < 60) return `${n}s`;
  if (n < 3600) return `${Math.floor(n / 60)}m ${n % 60}s`;
  return `${Math.floor(n / 3600)}h ${Math.floor((n % 3600) / 60)}m`;
};

const STATUS_META = {
  processing: { label: "Procesare în curs", icon: RefreshCw, color: "text-blue-600", spin: true },
  completed:  { label: "Finalizat cu succes", icon: CheckCircle2, color: "text-emerald-600" },
  partial:    { label: "Finalizat cu avertismente", icon: AlertCircle, color: "text-amber-600" },
  failed:     { label: "Eșuat", icon: XCircle, color: "text-red-600" },
  cancelled:  { label: "Anulat de utilizator", icon: Ban, color: "text-slate-600" },
  interrupted: { label: "Întrerupt (worker oprit) — poate fi reluat", icon: AlertCircle, color: "text-orange-600" },
};

export const ImportProgressWidget = ({
  batchId,
  onFinish,
  onCancel,
  intervalMs = 1500,
  showTitle = true,
  compact = false,
  testIdPrefix = "import-progress-widget",
}) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const finishedRef = useRef(false);
  const onFinishRef = useRef(onFinish);

  // Keep the latest onFinish in a ref so we can call it without retriggering the effect
  useEffect(() => { onFinishRef.current = onFinish; }, [onFinish]);

  useEffect(() => {
    if (!batchId) return undefined;
    let cancelled = false;
    finishedRef.current = false;

    const poll = async () => {
      try {
        const r = await axios.get(`${API_URL}/import/batches/${batchId}/live-progress`);
        if (cancelled) return;
        setData(r.data);
        setError(null);
        if (r.data?.is_terminal && !finishedRef.current) {
          finishedRef.current = true;
          onFinishRef.current?.(r.data);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.status === 404 ? "Batch negăsit" : "Eroare la citirea progresului");
      }
    };

    // Fire once immediately, then on interval — stop after terminal state.
    poll();
    const handle = setInterval(() => {
      if (finishedRef.current) return;  // stop polling once terminal
      poll();
    }, intervalMs);

    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [batchId, intervalMs]);

  if (error) {
    return (
      <div
        className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700"
        data-testid={`${testIdPrefix}-error`}
      >
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div
        className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-500"
        data-testid={`${testIdPrefix}-loading`}
      >
        Se încarcă progresul…
      </div>
    );
  }

  const meta = STATUS_META[data.status] || STATUS_META.processing;
  const Icon = meta.icon;
  const percent = data.percent ?? 0;
  const rowsPerMin = data.rows_per_second != null ? Math.round(data.rows_per_second * 60) : null;
  const padCls = compact ? "p-3" : "p-4";
  const gapCls = compact ? "space-y-2" : "space-y-3";
  const cancelHandler = data.is_terminal || data.cancellation_requested ? null : onCancel;

  return (
    <div
      className={`rounded-lg border bg-white shadow-sm ${padCls} ${gapCls}`}
      data-testid={testIdPrefix}
    >
      {showTitle && (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
              {data.source}
            </p>
            <p className="text-sm font-semibold text-slate-900 truncate" data-testid={`${testIdPrefix}-filename`}>
              {data.filename || "(fără fișier)"}
            </p>
          </div>
          <div className={`flex items-center gap-1.5 text-xs font-medium ${meta.color}`} data-testid={`${testIdPrefix}-status`}>
            <Icon className={`h-4 w-4 ${meta.spin ? "animate-spin" : ""}`} />
            {meta.label}
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <div className="flex justify-between text-sm font-medium">
          <span data-testid={`${testIdPrefix}-counter`} className="text-slate-700">
            {data.rows_processed.toLocaleString("ro-RO")} / {data.rows_total.toLocaleString("ro-RO")} rânduri
          </span>
          <span data-testid={`${testIdPrefix}-percent`} className="text-slate-700">
            {percent != null ? `${percent}%` : "—"}
          </span>
        </div>
        <Progress value={percent || 0} className="h-2" data-testid={`${testIdPrefix}-bar`} />
      </div>

      {!compact && (
        <div className="grid grid-cols-4 gap-2 text-center">
          <div className="bg-emerald-50 rounded p-2" data-testid={`${testIdPrefix}-created`}>
            <p className="text-base font-bold text-emerald-700">+{data.rows_created}</p>
            <p className="text-[10px] uppercase text-emerald-600 font-medium">Create</p>
          </div>
          <div className="bg-blue-50 rounded p-2" data-testid={`${testIdPrefix}-updated`}>
            <p className="text-base font-bold text-blue-700">{data.rows_updated}</p>
            <p className="text-[10px] uppercase text-blue-600 font-medium">Actualizate</p>
          </div>
          <div className="bg-slate-50 rounded p-2" data-testid={`${testIdPrefix}-skipped`}>
            <p className="text-base font-bold text-slate-500">{data.rows_skipped}</p>
            <p className="text-[10px] uppercase text-slate-500 font-medium">Ignorate</p>
          </div>
          <div className={`${data.rows_errors > 0 ? "bg-red-50" : "bg-slate-50"} rounded p-2`} data-testid={`${testIdPrefix}-errors`}>
            <p className={`text-base font-bold ${data.rows_errors > 0 ? "text-red-700" : "text-slate-500"}`}>
              {data.rows_errors}
            </p>
            <p className="text-[10px] uppercase text-slate-500 font-medium">Erori</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500 pt-1 border-t border-slate-100">
        <span data-testid={`${testIdPrefix}-elapsed`}>
          Timp scurs: <span className="font-medium text-slate-700">{formatDuration(data.elapsed_seconds)}</span>
        </span>
        {!data.is_terminal && (
          <span data-testid={`${testIdPrefix}-eta`}>
            ETA: <span className="font-medium text-slate-700">
              {data.eta_seconds != null ? formatDuration(data.eta_seconds) : "se calculează…"}
            </span>
          </span>
        )}
        {rowsPerMin != null && (
          <span data-testid={`${testIdPrefix}-rate`}>
            <span className="font-medium text-slate-700">~{rowsPerMin.toLocaleString("ro-RO")}</span> rânduri/min
          </span>
        )}
      </div>

      {cancelHandler && (
        <div className="pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={cancelHandler}
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid={`${testIdPrefix}-cancel-btn`}
          >
            <Ban className="h-3.5 w-3.5 mr-1.5" />
            Anulează importul
          </Button>
        </div>
      )}

      {data.cancellation_requested && !data.is_terminal && (
        <div
          className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
          data-testid={`${testIdPrefix}-cancel-requested`}
        >
          Anulare solicitată — importul se va opri la următorul checkpoint…
        </div>
      )}
    </div>
  );
};

export default ImportProgressWidget;
